import { Metadata } from "./Metadata";

export type BaseDto = {
  id: string;
  name: string;
  created: Date;
  createdBy: string;
  edited: Date;
  EditedBy: string;
  description?: string;
};

export enum TemplateCategory {
  DEFAULT = 0,
  CENTRAL_MONITORING = 1,
}

export type FormTemplate = {
  name: string;
  id: string;
  created: Date;
  isLocked: boolean;
  problemQuestions: ProblemAnswer[];
  footer: string;
  footerFul: string;
  studyId?: string;
  studyName?: string;
  templateType: TemplateCategory;
} & BaseDto;

export type TemplateEditableSection = {
  id: string;
  name: string;
  questions: EditableReportQuestion[];
  weight: number;
};

export enum TemplateType {
  METADATA = 1,
  STATUS = 2,
  PARTICIPANTS = 4,
  ISSUES = 8,
  QUESTIONS = 16,
  FOOTER = 32,
  FINALCOMMENT = 64,
  ACTIONS = 128,
  ATTACHMENTS = 256,
}

export enum ReportType {
  DEFAULT = 0,
  CENTRAL_MONITORING = 1,
}

export type Report = {
  id: string;
  address: string;
  templateId: string;
  templateName?: string;
  toVerifyDate: Date;
  study: string;
  studyId: string;
  resort: string;
  resortId: string;
  name: string;
  visitName?: string;
  visitNumber?: number;
  created: Date;
  state: ReportState;
  footer: string;
  footerFul: string;
  templateType: TemplateCategory;
  reportType: ReportType;
  sentToSponsorDate: string | null;
  tmfDate: string | null;
  cmAnalysisDate: string | null;
  cmReportDate: string | null;
  cmPmVerificationDate: string | null;
} & BaseDto;

export type EditableReportQuestion = {
  id: string;
  questionId: string;
  name: string;
  description: string;
  weight: number;
  descriptionId: string;
  isBlank: boolean;
  parentId: string;
  isDirectAnswer:boolean;
  created: Date;
  requiredAnswerToBeVisible: string;
  children: EditableReportQuestion[];
} & BaseDto;

export type QuestionDescriptionDTO = {
  id: string;
  description: string;
  created: Date;
} & BaseDto;

export type RaportSection = {
  id: string;
  name: string;
  questionsWithAnswer: Answer[];
  weight: number;
  questions: EditableReportQuestion;
};

export type ParticipantAnswer = {
  id: string;
  participantNumber: string;
  visitNumber: string;
  isFinshed: boolean;
  isVerified: boolean;
  comment: string;
} & BaseDto;

export type ReportFile = {
  id: string;
  name: string;
  label?: string;
} & BaseDto;

export type Answer = {
  questionId: string;
  parentId?: string;
  answerId: string;
  description: string;
  answer: string;
  comment: string;
  weight: number;
  isBlank: boolean;
  created: Date;
  requiredAnswerToBeVisible: string;
  isPresentInFollowUpLetter: boolean;
  isDirectAnswer: boolean;
  children: Answer[]; // not part of the response
};

export type ProblemAnswer = Answer & {
  descriptionId: number;
  categoryId: number;
  weight: number;
  problemType: ProblemType;
  category: string;
  personIdentifying: string;
  participantNumber: string;
  identificationDate: Date | null;
  resolvedDate: Date | null;
  author: string;
};

export type RowInput = {
  descriptionId: string;
};

export type Study = {
  id: string;
  name: string;
  description: string;
  resorts: Resort[];
  templates: FormTemplate[];
  created: Date;
} & BaseDto;

export type Resort = {
  id: string;
  name: string;
  address: string;
  reports: Report[];
} & BaseDto;

export type ReviewComment = {
  parentId: string;
  comment: string;
  state: number;
} & BaseDto;

export enum ReportState {
  INPROGRESS = 0,
  TO_VERIFY = 1,
  TO_CORRECT = 2,
  ACCEPTED = 3,
}

export type GetReportsBetweenDatesResult = {
  id: string;
  reportName: string;
  resortName: string;
  studyName: string;
  createdBy: string;
  state: ReportState;
  reportType: ReportType;
  visitDate: string | null;
  closedDate: string | null;
  inProgressDate: string | null;
  toVerifyDate: string | null;
  toCorrectDate: string | null;
  acceptedDate: string | null;
  confirmationLetterDate: string | null;
  followUpLetterDate: string | null;
  sentToSponsorDate: string | null;
  reportTmfDate: string | null;
  fulSentToResortDate: string | null;
  fulTmfDate: string | null;
};

export type QuestionDTO = {
  id: string;
  name: string;
  description: string;
  parentId: string;
  answers: AnswerDto[];
} & BaseDto;

export type QuestionDescriptionWithAnswersDTO = {
  id: string;
  description: string;
  questions: QuestionDTO[];
} & BaseDto;

export type QuestionWithAnswers = {
  id: string;
  name: string;
  description: string;
};

export type AnswerDto = {
  id: string;
  answer: string;
  name: string;
  description: string;
  reportName: string;
  resortName: string;
  reportId: string;
  comment: string;
  parentId: string;
} & BaseDto;

export interface LetterDto {
  studyId: string;
  studyName: string;
  reportId: string;
  reportName: string;
  resortName: string;
  resortId: string;
  mainResearcher: string;
  protocolNumber: string;
  resortNumber: string;
  introductionText: string;
  visitType: string;
  visitDay: string;
  visitHour: string;
  researchTeam: string;
  accessList: string;
  visitReason: string;
  croTeam: string;
  confirmationLetterDate: string | null;
}

export interface FollowUpLetterDto {
  reportId: string; // Guid in C# is represented as string in TypeScript
  studyId: string;
  studyName: string;
  reportName: string;
  visitDate?: string; // Nullable DateTime in C# is optional in TypeScript
  visitDateEnd: string;
  resortName: string;
  resortId: string;
  declaredForSiv: number;
  amountForScreening: number;
  amountOfRandomized: number;
  followUpLetterNumber: number;
  sponsor: string;
  introductionText?: string; // Nullable property in C# is optional in TypeScript
  visitType: string;
  croParticipants: string;
  resortParticipants: string;
  studyProgress: string;
  answers: AnswerDto[];
  followUpLetterDate: string | null;
  sentToResortDate: string | null;
  tmfDate: string | null;
  participants: FulParticipant[];
  protocolNumber: string;
  studyAcronym: string;
  finalComment?: string;
  declaredParticipants: number;
  screenFailure: number;
  duringTreatment: number;
  protocolDeviation: number;
  reportedSAE: number;
}

export interface FulParticipant {
  participantNumber: string;
  visitNumber: string;
  isFinshed: boolean;
  isVerified: boolean;
  comment: string;
}

export interface FollowUpLetterFile {
  id: string;
  reportId: string;
  name: string;
  uri: string;
  includeInPdf: boolean;
  label?: string;
}

export enum ProblemType {
  MAJOR = 0,
  MINOR = 1,
}

export type StudyReportSummary = {
  studyId: string;
  total: number;
  inProgress: number;
  toVerify: number;
  toCorrect: number;
  accepted: number;
};
